import { useMemo, useState } from 'react';
import type { MenuProps } from 'antd';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  HomeOutlined,
  CalendarOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  DatabaseOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { MenuItem } from '@bancobarigui/barihaus';

import { useAuth } from '@/contexts/auth.context';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { BankRoles, ProviderRoles, UserRole } from '@/shared/enums/UserRoles';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { inspectionService } from '@/services/inspection.service';
import { exportRowsToExcel } from '@/utils/inspectionExport';

export function useProtectedRouteContainer() {
  const { logout, user } = useAuth();
  const navigation = useNavigate();
  const { hasPermission, hasRole } = usePermissions();
  const [isExporting, setIsExporting] = useState(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);

  const allMenuItems: MenuItem[] = [
    {
      key: '/',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      permissions: [
        UserPermissions.home_view,
        UserPermissions.home_view_provider,
        UserPermissions.home_view_inspector
      ],
    },
    {
      key: '/calendar',
      label: 'Calendário de Vistorias',
      icon: <CalendarOutlined />,
      permissions: [
        UserPermissions.home_view,
        UserPermissions.home_view_provider,
        UserPermissions.home_view_inspector
      ],
    },
    ({
      key: '/metrics',
      label: 'Métricas',
      icon: <BarChartOutlined />,
      permissions: [
        UserPermissions.home_view,
        UserPermissions.home_view_provider
      ],
      roles: [
        BankRoles.ADMIN_BANK,
        ProviderRoles.MASTER_PROVIDER
      ] as UserRole[],
    } as any) as MenuItem,
    {
      key: '/checklists',
      label: 'Checklists',
      icon: <CheckSquareOutlined />,
      permissions: [
        UserPermissions.checklist_read_inspector,
        UserPermissions.checklist_read_provider,
        UserPermissions.checklist_read_bank
      ],
    },
    {
      key: 'sub1',
      label: 'Cadastros',
      icon: <DatabaseOutlined />,
      children: [
        {
          key: 'g1',
          label: 'Usuários',
          children: [
            {
              key: '/bank-list',
              label: 'Banco',
              permissions: [UserPermissions.users_read_bank, UserPermissions.users_create_bank],
            },
            {
              key: '/provider-list',
              label: 'Prestadora',
              permissions: [UserPermissions.users_read_provider, UserPermissions.users_create_provider],
            },
            {
              key: '/inspector-list',
              label: 'Inspetor',
              permissions: [
                UserPermissions.inspectors_read_provider,
                UserPermissions.inspectors_read_bank,
                UserPermissions.inspectors_create_provider,
                UserPermissions.inspectors_create_bank
              ],
            },
          ],
        },
        {
          key: '/vendor-list',
          label: 'Prestadora',
          permissions: [UserPermissions.providers_read_bank, UserPermissions.providers_create_bank],
        },
        {
          key: 'g2',
          label: 'Parametrizações',
          children: [
            {
              key: '/inspection-type-list',
              label: 'Tipo de Inspeção',
              permissions: [UserPermissions.inspections_create_bank],
            },
            {
              key: '/property-type-list',
              label: 'Tipo de Imóvel',
              permissions: [UserPermissions.inspections_create_bank],
            },
            {
              key: '/inspection-reason-list',
              label: 'Motivo da Inspeção',
              permissions: [UserPermissions.inspections_create_bank],
            },
            {
              key: '/inspection-issue-type-list',
              label: 'Tipos de Pendência',
              permissions: [UserPermissions.inspections_create_bank],
            },
          ],
        },
      ],
    },
    {
      key: 'sub-inspections',
      label: 'Inspeções',
      icon: <SearchOutlined />,
      children: [
        {
          key: '/dashboard/all_inspections',
          label: 'Listar Inspeções',
          permissions: [
            UserPermissions.inspections_read_bank,
            UserPermissions.inspections_read_provider,
            UserPermissions.home_view,
            UserPermissions.home_view_provider,
          ],
        },
        {
          key: '/request-inspection',
          label: 'Criar Inspeção',
          permissions: [UserPermissions.inspections_create_bank],
        },
      ],
    },
  ];

  const filteredMenuItems = useMemo(() => {
    const filterMenu = (items: MenuItem[]): MenuItem[] => {
      return items.reduce<MenuItem[]>((allowedItems, currentItem) => {
        if (currentItem.children && currentItem.children.length > 0) {
          const filteredChildren = filterMenu(currentItem.children);
          if (filteredChildren.length > 0) {
            allowedItems.push({ ...currentItem, children: filteredChildren });
          }
        } else {
          
          const hasNoRestrictions = (!currentItem.permissions || currentItem.permissions.length === 0) &&
                                    (!(currentItem as any).roles || (currentItem as any).roles.length === 0);

          if (hasNoRestrictions) {
            allowedItems.push(currentItem);
          } else {
            
            const hasValidPermission = !currentItem.permissions || currentItem.permissions.length === 0 ||
                                       hasPermission(currentItem.permissions as UserPermissions[]);

            
            const hasValidRole = !(currentItem as any).roles || (currentItem as any).roles.length === 0 ||
                                hasRole((currentItem as any).roles as UserRole[]);

            
            if (hasValidPermission && hasValidRole) {
              allowedItems.push(currentItem);
            }
          }
        }
        return allowedItems;
      }, []);
    };

    return filterMenu(allMenuItems);
  }, [hasPermission, hasRole]);

  const onClickMenu = (key: string) => {
    navigation(key);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
    }
  };

  const handleProfile = () => {
    navigation('/profile');
  };

  const handleExportPanorama = async (filters?: {
    statuses?: string[];
    inspectionTypeIds?: string[];
    inspectionReasonIds?: string[];
    providerIds?: string[];
    inspectorIds?: string[];
    reportAuthorIds?: string[];
    states?: string[];
    cities?: string[];
    isScheduled?: boolean;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Buscando inspeções...', key: 'export' });

      // Endpoint dedicado de exportação: uma única chamada devolve as linhas já
      // achatadas, montadas no backend com consultas em lote — sem paginar a listagem
      // completa nem rebuscar cada inspeção por ID (que estourava o timeout).
      // Timeout estendido (5 min) para bases muito grandes.
      const { rows, truncated, maxRows } = await inspectionService.getInspectionExportRows(filters, { timeoutMs: 300000 });

      if (rows.length > 0) {
        message.loading({ content: 'Gerando arquivo Excel...', key: 'export' });
        exportRowsToExcel(rows, `PANORAMA_AVALIACOES_${new Date().getFullYear()}.xlsx`);

        if (truncated) {
          // O backend cortou no teto: avisa em vez de deixar o usuário achar que a planilha está completa
          message.warning({
            content: `Relatório gerado com ${rows.length} vistorias, mas o limite de ${maxRows ?? rows.length} linhas foi atingido e o restante ficou de fora. Refine os filtros (ex.: período menor) e exporte novamente.`,
            key: 'export',
            duration: 10,
          });
        } else {
          message.success({ content: `Relatório gerado com sucesso! ${rows.length} vistorias exportadas.`, key: 'export', duration: 5 });
        }
      } else {
        message.warning({ content: 'Nenhuma vistoria encontrada para exportar.', key: 'export' });
      }
    } catch (error) {
      message.error({ content: `Erro ao gerar relatório: ${(error as Error).message}`, key: 'export' });
    } finally {
      setIsExporting(false);
    }
  };

  
  const canExportPanorama = user?.roles?.some(role =>
    role === BankRoles.ADMIN_BANK || role === BankRoles.ENG_MANAGEMENT
  );

  const handleOpenReportsModal = () => {
    setIsReportsModalOpen(true);
  };

  const handleCloseReportsModal = () => {
    setIsReportsModalOpen(false);
  };

  const dropDownItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Perfil',
      onClick: handleProfile,
    },
    ...(canExportPanorama ? [{
      key: 'reports',
      label: 'Relatório',
      onClick: handleOpenReportsModal,
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      danger: true,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return {
    onClickMenu,
    handleLogout,
    dropDownItems,
    menuItems: filteredMenuItems,
    isReportsModalOpen,
    handleCloseReportsModal,
    handleExportPanorama,
    isExporting,
  };
}
